import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { performSignOut } from '@/features/auth/lib/sign-out';
import { useAuthStore, useIsSSI } from '@/stores/auth.store';

/** Non-SSI idle timeout — matches auth.session.idle_timeout_minutes default. */
const IDLE_TIMEOUT_MS = 60 * 60 * 1000;
/** Non-SSI hard cap — matches auth.session.hard_timeout_hours default. */
const HARD_TIMEOUT_MS = 12 * 60 * 60 * 1000;
const WARNING_BEFORE_MS = 2 * 60 * 1000;

/**
 * Role-conditional session timeout (Auth V3 / D6).
 *
 * SSI staff (SSIAdministrator / SSIEmployee / Admin) are exempt from idle and
 * hard timeouts — sessions end only on explicit logout. Non-SSI users get a
 * ~60 min idle warning + logout and a 12h hard cap from session start.
 */
export function SessionTimeoutModal() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const role = useAuthStore((s) => s.user?.role);
  const isSsi = useIsSSI();
  const navigate = useNavigate();
  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const warningTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const logoutTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hardTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const countdownRef = useRef<ReturnType<typeof setInterval>>(undefined);
  const sessionStartedAtRef = useRef<number>(Date.now());

  const forceLogout = useCallback(() => {
    void performSignOut().finally(() => {
      navigate('/login');
    });
  }, [navigate]);

  const clearAllTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearTimeout(hardTimerRef.current);
    clearInterval(countdownRef.current);
  }, []);

  const resetIdleTimers = useCallback(() => {
    setShowWarning(false);
    clearTimeout(warningTimerRef.current);
    clearTimeout(logoutTimerRef.current);
    clearInterval(countdownRef.current);

    // SSI never idle-timeouts. Unknown role fails closed (no timers) so SSI is never
    // armed before /auth/me hydrates role (DEF-418 residual).
    if (!isAuthenticated || !role || isSsi) return;

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setSecondsLeft(Math.floor(WARNING_BEFORE_MS / 1000));
      countdownRef.current = setInterval(() => {
        setSecondsLeft((prev) => Math.max(0, prev - 1));
      }, 1000);
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    logoutTimerRef.current = setTimeout(() => {
      forceLogout();
    }, IDLE_TIMEOUT_MS);
  }, [isAuthenticated, role, isSsi, forceLogout]);

  useEffect(() => {
    if (!isAuthenticated) return;
    // SSI / unknown role: no idle / hard timers.
    if (!role || isSsi) {
      clearAllTimers();
      setShowWarning(false);
      return;
    }

    sessionStartedAtRef.current = Date.now();
    const remainingHard = Math.max(
      0,
      HARD_TIMEOUT_MS - (Date.now() - sessionStartedAtRef.current),
    );
    hardTimerRef.current = setTimeout(() => {
      forceLogout();
    }, remainingHard);

    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;
    const handler = () => resetIdleTimers();

    events.forEach((e) => window.addEventListener(e, handler, { passive: true }));
    resetIdleTimers();

    return () => {
      events.forEach((e) => window.removeEventListener(e, handler));
      clearAllTimers();
    };
  }, [isAuthenticated, role, isSsi, resetIdleTimers, forceLogout, clearAllTimers]);

  if (!role || isSsi || !showWarning) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80">
      <div className="mx-4 w-full max-w-sm rounded-lg border border-border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warning-bg text-warning">
            <Clock className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">Session Expiring</h3>
            <p className="text-sm text-muted-foreground">
              {minutes}:{seconds.toString().padStart(2, '0')} remaining
            </p>
          </div>
        </div>
        <p className="mb-5 text-sm text-muted-foreground">
          Your session will expire due to inactivity. Click below to stay signed in.
        </p>
        <div className="flex gap-3">
          <Button type="button" onClick={resetIdleTimers} className="flex-1">
            Stay Signed In
          </Button>
          <Button type="button" variant="outline" onClick={forceLogout}>
            Sign Out
          </Button>
        </div>
      </div>
    </div>
  );
}
